/**
 * Format a string amount for display in a given currency.
 *
 * `currency` is an ISO 4217 code ("USD", "EUR") as the API carries it on an
 * account or a report. `null` means the server named no currency (or lines
 * disagreed): the figure is shown with two decimals and no symbol, never with
 * an assumed one. Omitted, it is "USD": the review list's rows carry no
 * currency in the API, so they keep the format they had.
 *
 * IMPORTANT: parseFloat is used here ONLY for display formatting via
 * toLocaleString, which rounds to 2 decimal places. No arithmetic
 * (addition, subtraction, comparison) may be performed on the parsed
 * float value. For arithmetic on amounts, use integer cents or a
 * decimal library.
 */
export function formatAmount(amount: string | null, currency: string | null = "USD"): string {
  if (amount === null || amount === undefined) {
    return "--";
  }
  const num = parseFloat(amount);
  if (isNaN(num)) {
    return "--";
  }
  const abs = Math.abs(num);
  const formatted = abs.toLocaleString("en-US", numberFormat(currency));
  return amount.startsWith("-") ? `-${formatted}` : formatted;
}

function numberFormat(currency: string | null): Intl.NumberFormatOptions {
  if (currency === null) {
    return { minimumFractionDigits: 2, maximumFractionDigits: 2 };
  }
  return { style: "currency", currency };
}

/**
 * Check if a string amount is negative using string inspection.
 * Does NOT use parseFloat -- avoids float precision issues.
 */
export function isNegativeAmount(amount: string | null): boolean {
  if (amount === null || amount === undefined) {
    return false;
  }
  return amount.startsWith("-");
}
